/**
 * Generic option fields (media + bounds) from interactionCatalog for enum types.
 *
 * Layout: Media on its own row; type-specific numeric/select bounds on the row
 * below in an even auto-fit grid (all 12 enum types share this pattern).
 */

import './InteractionOptionsPanel.css';

import * as React from 'react';
import {
    cn,
    Input,
    Label,
    RadioGroup,
    RadioGroupItem,
    Card,
    CardContent,
} from '@ai-content-hub/content-kit';
import { Sparkles } from 'lucide-react';
import type { InteractionOptionField } from '../interactionCatalog';
import { getInteractionOptionFields } from '../interactionOptions';
import { mediaOptionLabel, usePromptTranslations, type PromptTranslations } from '../i18n';

// EAA-2031: labels come from the prompt i18n table, not the catalog's English fallback.
function fieldLabel(field: InteractionOptionField, t: PromptTranslations): string {
    return field.labelKey ? t[field.labelKey] : field.label;
}

export interface InteractionOptionsPanelProps {
    questionType: string;
    values: Record<string, string | number>;
    onFieldChange: (key: string, value: string | number) => void;
    disabled?: boolean;
    className?: string;
}

function splitOptionFields(fields: InteractionOptionField[]) {
    const media = fields.find((f) => f.key === 'media');
    const config = fields.filter((f) => f.key !== 'media');
    return { media, config };
}

function renderMediaField(
    field: InteractionOptionField,
    values: Record<string, string | number>,
    onFieldChange: (key: string, value: string | number) => void,
    disabled: boolean,
    t: PromptTranslations,
) {
    const current = String(values[field.key] ?? field.defaultValue);
    return (
        <div className="interaction-options-panel-media-row flex min-w-0 flex-col gap-2.5">
            <Label className="text-xs font-medium tracking-wider text-muted-foreground">
                {fieldLabel(field, t)}
            </Label>
            <RadioGroup
                value={current}
                onValueChange={(v) => onFieldChange(field.key, v)}
                disabled={disabled}
                className="flex w-full flex-wrap gap-2"
            >
                {(field.options ?? []).map((opt) => (
                    <label
                        key={String(opt.value)}
                        className={cn(
                            'interaction-options-panel-media-option interaction-options-panel-media-chip flex cursor-pointer items-center gap-2 rounded-lg border-2 px-4 py-2 text-sm font-medium transition-colors duration-200',
                            current === String(opt.value)
                                ? 'border-primary bg-primary/10 text-primary shadow-sm'
                                : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
                            disabled && 'cursor-not-allowed opacity-50',
                        )}
                    >
                        <RadioGroupItem value={String(opt.value)} className="h-4 w-4 shrink-0 border-2" />
                        {field.key === 'media' ? mediaOptionLabel(String(opt.value), t) : opt.label}
                    </label>
                ))}
            </RadioGroup>
        </div>
    );
}

function renderNumberField(
    field: InteractionOptionField,
    values: Record<string, string | number>,
    onFieldChange: (key: string, value: string | number) => void,
    disabled: boolean,
    t: PromptTranslations,
) {
    const numVal = values[field.key] ?? field.defaultValue;
    return (
        <div
            key={field.key}
            className="interaction-options-panel-field interaction-options-panel-field--number flex min-w-0 flex-col gap-2"
        >
            <Label
                htmlFor={`interaction-opt-${field.key}`}
                className="text-xs font-medium tracking-wider text-muted-foreground"
            >
                {fieldLabel(field, t)}
            </Label>
            <Input
                id={`interaction-opt-${field.key}`}
                type="number"
                min={field.min}
                max={field.max}
                value={numVal}
                disabled={disabled}
                className="interaction-options-panel-number-input h-10 w-full max-w-none rounded-md"
                onChange={(e) => {
                    const parsed = Number(e.target.value);
                    if (Number.isNaN(parsed)) return;
                    let next = parsed;
                    if (field.min != null) next = Math.max(field.min, next);
                    if (field.max != null) next = Math.min(field.max, next);
                    onFieldChange(field.key, next);
                }}
            />
        </div>
    );
}

export const InteractionOptionsPanel: React.FC<InteractionOptionsPanelProps> = ({
    questionType,
    values,
    onFieldChange,
    disabled = false,
    className,
}) => {
    const t = usePromptTranslations();
    const fields = getInteractionOptionFields(questionType);
    if (fields.length === 0) {
        return null;
    }

    const { media, config } = splitOptionFields(fields);

    return (
        <div
            className={cn(
                'interaction-options-panel border-b border-border/60 sm:px-5',
                className,
            )}
        >
            <Card className="interaction-options-panel-card border-0 bg-muted/15 shadow-none py-0">
                <CardContent className="flex flex-col gap-4 p-4 sm:p-5">
                    <h3 className="interaction-options-panel-title flex items-center gap-2 text-sm font-semibold tracking-tight text-foreground">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <Sparkles className="h-4 w-4" />
                        </span>
                        {t.questionSetup}
                    </h3>

                    <div className="interaction-options-panel-body flex flex-col gap-4">
                        {media ? renderMediaField(media, values, onFieldChange, disabled, t) : null}

                        {config.length > 0 ? (
                            <div className="interaction-options-config-grid">
                                {config.map((field) =>
                                    field.type === 'select' && field.options?.length
                                        ? renderMediaField(field, values, onFieldChange, disabled, t)
                                        : renderNumberField(field, values, onFieldChange, disabled, t),
                                )}
                            </div>
                        ) : null}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
